// app/src/lib/cashflow/apAging.js — AP aging (US-3.2).
//
// Buckets: current, 1-7, 8-14, 15-30, >30. Statuses considered "outstanding":
// RECEIVED, PAID_LOCAL, DISPUTED, OVERDUE.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { daysBetween, isoToday } from './util.js';
import { vendorLabel } from '../demo.js';

const BUCKETS = [
  { id: 'current',  label: 'Current',          min: -Infinity, max: 0 },
  { id: 'd1_7',     label: '1–7 days',         min: 1,         max: 7 },
  { id: 'd8_14',    label: '8–14 days',        min: 8,         max: 14 },
  { id: 'd15_30',   label: '15–30 days',       min: 15,        max: 30 },
  { id: 'over30',   label: 'Over 30 days',     min: 31,        max: Infinity },
];

const OPEN_STATUSES = new Set(['RECEIVED', 'PAID_LOCAL', 'DISPUTED', 'OVERDUE']);

/**
 * @returns {Promise<{ asOf:string, buckets:Array<{ id:string, label:string, totalCents:number, rows:any[] }>, totalCents:number }>}
 */
export async function build() {
  const userId = await requireUserId();
  const today = isoToday();

  const invoices = await db.invoices
    .where('userId').equals(userId)
    .filter((i) => OPEN_STATUSES.has(i.status))
    .toArray();

  const vendorIds = Array.from(new Set(invoices.map((i) => i.vendorId).filter(Boolean)));
  const vendors = await db.vendors.where('vendorId').anyOf(vendorIds).toArray();
  const vendorById = new Map(vendors.map((v) => [v.vendorId, v]));

  const buckets = BUCKETS.map((b) => ({ ...b, totalCents: 0, rows: [] }));
  let total = 0;

  for (const inv of invoices) {
    const dueDate = inv.dueDate || inv.issuedDate;
    const daysOverdue = daysBetween(today, dueDate); // positive = past due
    const bucket = buckets.find((b) => daysOverdue >= b.min && daysOverdue <= b.max);
    if (!bucket) continue;
    const row = {
      invoiceId: inv.invoiceId,
      vendorName: vendorLabel(vendorById.get(inv.vendorId)?.name),
      invoiceNumber: inv.invoiceNumber || '',
      issuedDate: inv.issuedDate,
      dueDate,
      amountCents: inv.totalCents || 0,
      daysOverdue: Math.max(0, daysOverdue),
      status: inv.status,
      plaidTxId: inv.plaidTxId,
    };
    bucket.rows.push(row);
    bucket.totalCents += row.amountCents;
    total += row.amountCents;
  }

  for (const b of buckets) {
    b.rows.sort((a, b) => (b.daysOverdue - a.daysOverdue) || a.dueDate.localeCompare(b.dueDate));
  }

  return { asOf: today, buckets, totalCents: total };
}

/** CSV serialization. Returns a string the UI can pass to Blob/download. */
export async function exportCsv() {
  const aging = await build();
  const lines = ['vendor,invoice_number,issued,due,amount,days_overdue,status,bucket'];
  for (const b of aging.buckets) {
    for (const r of b.rows) {
      lines.push([
        csvField(r.vendorName),
        csvField(r.invoiceNumber),
        r.issuedDate,
        r.dueDate,
        (r.amountCents / 100).toFixed(2),
        r.daysOverdue,
        r.status,
        b.id,
      ].join(','));
    }
  }
  return lines.join('\n');
}

function csvField(s) {
  if (s == null) return '';
  // CSV-injection defense: spreadsheets execute formulas on fields starting with
  // = + - @ or whitespace control chars. Prefix a literal apostrophe so the
  // formula is rendered as text.
  let str = String(s);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

/** Returns invoices that should be marked OVERDUE (PAID_LOCAL past dueDate + 3 days). */
export async function findOverdueCandidates() {
  const userId = await requireUserId();
  const today = isoToday();
  const open = await db.invoices.where('[userId+status]').equals([userId, 'PAID_LOCAL']).toArray();
  return open.filter((i) => i.dueDate && daysBetween(today, i.dueDate) > 3);
}
